import { Stack } from "@introist/react-foundation/v2";
import { useParams } from "react-router-dom";

import { JourneyStepV2, api } from "services/rpc/RpcProvider";
import { ListView, TopBarRoute } from "components/templates";
import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { JourneyStepTable } from "../../../journeys/JourneyStepTable";
import { BulkCancelButton } from "../../../journeys/JourneyStepTable/BulkCancelButton";
import { useSearch } from "../../../../foundation";
import { AutomationHeader } from "../../components/AutomationHeader";
import { AutomationStepStatuses } from "./AutomationStepStatuses";
import { Skeletons } from "../../../../components/molecules";
import { AUTOMATION_ROUTES } from "../index";
import { AutomationStepDrawer } from "../../containers/AutomationStepDrawer";

export const AutomationRoute = () => {
  const navigate = useNavigate();
  const { stepId, automationId } = useParams();

  const { data: automation, refetch: refetchAutomation } = api.automations.find.useQuery({
    id: automationId!
  });

  const { data: journeySteps, refetch: refetchSteps } = api.journeys.steps.list.useQuery(
    { journeyId: automationId! },
    {
      enabled: !!automationId
    }
  );

  const refetch = useCallback(async () => {
    await refetchSteps();
    await refetchAutomation();
  }, [refetchSteps, refetchAutomation]);

  const {
    filtered: filteredJourneySteps,
    search,
    setSearch
  } = useSearch(journeySteps, {
    fields: ["title", "stepType"]
  });

  const [statusFilter, setStatusFilter] = useState<string[] | null>(null);
  const handleFilterChange = useCallback((statuses: string[]) => setStatusFilter(statuses), []);

  const displaySteps = useMemo(() => {
    if (!filteredJourneySteps || statusFilter === null) return filteredJourneySteps;
    return (filteredJourneySteps as JourneyStepV2[]).filter(s => statusFilter.includes(s.status));
  }, [filteredJourneySteps, statusFilter]);

  // State for bulk selection
  const [selectedSteps, setSelectedSteps] = useState<string[]>([]);

  // Only show bulk selection when draft status is in the filter
  const showBulkSelection = useMemo(() => {
    return statusFilter === null || statusFilter.includes("draft");
  }, [statusFilter]);

  const handleSelectionChange = useCallback((steps: string[]) => {
    setSelectedSteps(steps);
  }, []);

  const handleBulkCancelCompleted = useCallback(() => {
    setSelectedSteps([]);
    refetch();
  }, [refetch]);

  const paths = useMemo(() => {
    const paths = [];
    paths.push({
      key: "automations",
      title: `Automations`,
      onClick: () => navigate("/automations")
    });

    paths.push({
      key: "employee",
      title: automation?.employeeName ?? "",
      onClick: () => navigate(`/employees/${automation?.employeeId}`)
    });

    paths.push({
      key: "automation",
      title: automation?.title ?? ""
    });

    return paths;
  }, [automation?.employeeId, automation?.employeeName, automation?.title, navigate]);

  return (
    <TopBarRoute paths={paths} medium>
      <Stack vertical gap="large" style={{ flex: 1 }}>
        {automation && <AutomationHeader automation={automation} onUpdate={() => refetch()} />}

        <ListView.Header
          title="Automation steps"
          search={{
            search,
            setSearch
          }}
          left={
            <AutomationStepStatuses
              steps={filteredJourneySteps}
              onFilterChange={handleFilterChange}
            />
          }
        />
        {automation && (
          <JourneyStepTable
            automation={automation}
            journeySteps={displaySteps as JourneyStepV2[]}
            selectedSteps={selectedSteps}
            onSelectionChange={showBulkSelection ? handleSelectionChange : undefined}
          />
        )}
        {automation && !journeySteps && <Skeletons.List />}
        {showBulkSelection && (
          <BulkCancelButton
            selectedSteps={selectedSteps}
            onCompleted={handleBulkCancelCompleted}
          />
        )}
      </Stack>
      {journeySteps && (
        <AutomationStepDrawer
          open={!!stepId}
          onClose={() =>
            navigate(AUTOMATION_ROUTES.automation.path.replace(":automationId", automationId!))
          }
          stepId={stepId}
        />
      )}
    </TopBarRoute>
  );
};
